import React, { useEffect, useRef, useState } from 'react';
import { DatePicker } from 'antd';
import moment from 'moment';
import { DISPLAY_DATE_FORMAT, SERVER_DATE_FORMAT } from '@shared/constants/date';

const FORMAT_CONFIG = {
	date: { server: SERVER_DATE_FORMAT, display: DISPLAY_DATE_FORMAT },
	year: { server: 'YYYY', display: 'YYYY' },
};

// Fecha actual
const currentDate = moment().startOf('day');

/**
 * Componente: Input de fecha que utiliza el componente DatePicker de Ant Design.
 * Se extienden las propiedades del componente [DatePicker](https://ant.design/components/date-picker#datepicker)
 * 
 * @param useCurrentDate Indica si debe inicializar el input con la fecha actual si no se proporciona un valor inicial (default: false).
 */

export const InputDate = ({ value, onChange, format, useCurrentDate = false, picker, ...extraProps }) => {
	const { server: serverFormat, display: displayFormat } = FORMAT_CONFIG[picker] ?? { server: SERVER_DATE_FORMAT, display: DISPLAY_DATE_FORMAT };

	const [lastDateString, setLastDateString] = useState(
		value && typeof value !== 'string'
			? value.format(serverFormat)
			: value || '',
	);

	const isFirstRender = useRef(true);
	let formattedValue = null;
	let formattedValueString = '';

	if (value) {
		// Si hay un valor proporcionado, se formatea
		formattedValue = moment(value, serverFormat);
		formattedValueString = formattedValue.format(serverFormat);
	} else if (isFirstRender.current && useCurrentDate) {
		// Si es el primer render y useCurrentDate es true, se asigna la fecha actual
		formattedValue = currentDate;
		formattedValueString = formattedValue.format(serverFormat);
	}

	// Función para manejar los cambios en el DatePicker de Antd
	const handleChange = (date/*, dateString*/) => {
		const dateString = date ? date.format(serverFormat) : null;
		// Se invierte la salida para enviar la fecha en formato string como primer argumento al formulario
		onChange?.(dateString, date);
		setLastDateString(dateString);
	};

	// Maneja los cambios en el valor formateado durante el primer render y en cada modificación externa del valor del input
	useEffect(() => {
		if (formattedValueString !== lastDateString) {
			handleChange(formattedValue, formattedValueString);
		}

		if (isFirstRender.current) isFirstRender.current = false;
	}, [value]);

	return (
		<DatePicker
			value={formattedValue}
			onChange={handleChange}
			format={displayFormat}
			picker={picker}
			style={{ width: '100%' }}
			{...extraProps}
		/>
	);
};